import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { BBox } from '@turf/helpers';

import { AutocompletePlace } from '../../components/PlacesAutocomplete';
import { GeoFilter } from '../../contexts/FilterContext';
import { GeoFilter as GeoFilterGraphQl, PeopleSnaphostGeoResult, PersonFilterInput } from '../../types/api.graphql';
import { createGeoFilterHelper } from '.';

export interface DrawProps {
  map: mapboxgl.Map;
  drawer: MapboxDraw;
  color: string;
}

export interface MarkerProps {
  map: mapboxgl.Map;
  position: number[];
  markerImg: HTMLImageElement;
}

export abstract class GeoFilterHelper<T = any> {
  protected geoFilter: GeoFilter<T>;
  public geoFilterGraphQL?: GeoFilterGraphQl;

  public constructor(geoFilter?: GeoFilter<T>) {
    this.geoFilter = geoFilter;
  }

  public static isEqualFilters(geoFilter1: GeoFilter, geoFilter2: GeoFilter): boolean {
    if (geoFilter1.type !== geoFilter2.type) return false;
    return createGeoFilterHelper(geoFilter1).isEqual(geoFilter2);
  }

  private static mergeBoundingBoxes(boundingBoxes: BBox[]) {
    let minLeft = 180;
    let minBottom = 90;
    let maxRight = -180;
    let maxTop = -90;

    boundingBoxes.forEach(([left, bottom, right, top]) => {
      if (left < minLeft) minLeft = left;
      if (bottom < minBottom) minBottom = bottom;
      if (right > maxRight) maxRight = right;
      if (top > maxTop) maxTop = top;
    });

    return [
      [minLeft, minBottom],
      [maxRight, maxTop],
    ];
  }

  public static filtersBoundingBox(geoFilters: GeoFilter[]) {
    if (geoFilters?.length > 1) {
      const geoFiltersBoundingBoxes = geoFilters.map((filter) => {
        return createGeoFilterHelper(filter).getBoundingBox();
      });

      const finalBoundingBox = this.mergeBoundingBoxes(geoFiltersBoundingBoxes);
      return finalBoundingBox;
    } else if (geoFilters?.length === 1) {
      const bbox = createGeoFilterHelper(geoFilters[0]).getBoundingBox();
      return bbox;
    }

    return null;
  }

  abstract generateId(): number;
  isEqual(geoFilter: GeoFilter): boolean {
    if (geoFilter.type !== this.geoFilter.type || geoFilter.isParent !== this.geoFilter.isParent) return false;

    return true;
  }
  abstract toGraphQLGeoFilter(skipExcludedZips?: boolean): PersonFilterInput;
  abstract generateIdForGeoFilterGraphQl(): number;
  abstract fromGraphQLGeoFilter(): GeoFilter<T>;
  getLocation(): AutocompletePlace {
    return undefined;
  }
  getFullAddress(): string {
    return undefined;
  }
  getInfoText(): string {
    return undefined;
  }
  abstract getCenter(): { longitude: number; latitude: number };
  abstract getBoundingBox(): BBox;
  abstract drawLayer(props: DrawProps): { layerFillId?: string };
  abstract drawMarker(props: MarkerProps): any;

  geoFilterFromGeoResult = (geoResult: PeopleSnaphostGeoResult, countByPostalCode: object): GeoFilter => ({
    ...this.geoFilter,
    id: this.geoFilter.id || this.generateId(),
    zipCodes: geoResult.postalCodes.map((postalCode) => ({
      zip: postalCode,
      count: countByPostalCode[postalCode] || 0,
    })),
    count: geoResult.count,
  });
}

export default GeoFilterHelper;
